import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Card,
  CardContent,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { ColumnConfig } from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/table';
import {
  toastError,
  toastSuccess,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { Users } from 'lucide-react';
import { DateTime } from 'luxon';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import Breadcrumbs from '@Components/Breadcrumbs';
import Heading from '@Components/Heading';
import DeleteInviteCodeDialog from '@Components/InviteCodes/DeleteInviteCodeDialog';
import InviteCodeSheet from '@Components/InviteCodes/InviteCodeSheet';
import {
  WINDOW_UNIT_LABELS,
  toInviteCodeFormValues,
  type InviteCodeFormValues,
} from '@Components/InviteCodes/inviteCodeForm';
import NotFound from '@Components/NotFound';
import { ResourceTableView } from '@Components/ResourceTable';
import { useResourceTableController } from '@Components/ResourceTable/useResourceTableController';
import ShowFor from '@Components/ShowFor';
import { useInviteCode } from '@Hooks/useInviteCode';
import { useInviteCodeSubscriptions } from '@Hooks/useInviteCodeSubscriptions';
import { useInviteCodeUsage } from '@Hooks/useInviteCodeUsage';
import type { RegistrationCodeSubscription } from '@Types/registrationCode';
import { formatCurrency, formatPhoneNumberFriendlyDisplay } from '@Utils/utils';

type DiscountValues = InviteCodeFormValues['signupFee'];
type WindowValues = Pick<
  InviteCodeFormValues['renewalFee'],
  'windowKind' | 'windowAmount' | 'windowUnit'
>;

/** Describes a discount in the sheet's terms; null when there is none. */
const describeDiscount = ({ kind, amount }: DiscountValues): string | null => {
  const value = Number(amount);
  switch (kind) {
    case 'PERCENT':
      return `${value}% off`;
    case 'AMOUNT_OFF':
      return `${formatCurrency(value)} off`;
    case 'SET_PRICE':
      return `Set to ${formatCurrency(value)}`;
    case 'FREE':
      return 'Free';
    default:
      return null;
  }
};

const describeWindow = ({ windowKind, windowAmount, windowUnit }: WindowValues) => {
  if (windowKind === 'FOREVER') return 'Forever';
  const amount = Number(windowAmount);
  const unit = WINDOW_UNIT_LABELS[windowUnit];
  return `${amount} ${amount === 1 ? unit.replace(/s$/, '') : unit} after joining`;
};

/**
 * Describes a wallet credit adjustment, which is applied on top of the deposit the wallet is
 * credited (the undiscounted wallet price); null when there is none.
 */
const describeAdjustment = (amount: string): string | null => {
  const value = Number(amount);
  if (!value) return null;
  return value > 0
    ? `+${formatCurrency(value)}`
    : `−${formatCurrency(Math.abs(value))}`;
};

function PricingValue({ value }: { value: string | null }) {
  return value ?? <span className="text-zinc-400">-</span>;
}

function DetailField({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={className}>
      <div className="text-sm font-bold text-zinc-500">{label}</div>
      {children}
    </div>
  );
}

const getMemberName = (row: RegistrationCodeSubscription) => {
  const profile = row.fish?.fishProfiles?.[0];
  const name = [profile?.fName, profile?.lName].filter(Boolean).join(' ');
  return name || '-';
};

const signupColumnConfigs: ColumnConfig<RegistrationCodeSubscription>[] = [
  {
    id: 'name',
    headerLabel: 'Member',
    accessor: (row) => getMemberName(row),
    type: 'text',
    size: 180,
    enableSorting: false,
  },
  {
    id: 'phoneNumber',
    headerLabel: 'Phone',
    accessor: (row) =>
      row.fish?.phoneNumber
        ? formatPhoneNumberFriendlyDisplay(row.fish.phoneNumber)
        : '-',
    type: 'text',
    size: 140,
    enableSorting: false,
  },
  {
    id: 'subscriptionTier',
    headerLabel: 'Tier',
    accessor: (row) => row.subscriptionTier?.title ?? '-',
    type: 'text',
    size: 160,
    enableSorting: false,
  },
  {
    id: 'status',
    headerLabel: 'Status',
    accessor: (row) => (row.deletedAt || row.cancelledAt ? 'Cancelled' : 'Active'),
    type: 'text',
    size: 120,
    enableSorting: false,
  },
  {
    id: 'createdAt',
    headerLabel: 'Signed Up',
    accessor: (row) =>
      DateTime.fromISO(row.createdAt).toLocaleString(DateTime.DATETIME_MED),
    type: 'text',
    size: 180,
    enableSorting: false,
  },
];

function InviteCodeSignupList({ inviteCodeId }: { inviteCodeId: string }) {
  const navigate = useNavigate();

  const controller = useResourceTableController<RegistrationCodeSubscription>({
    columnsConfig: signupColumnConfigs,
    defaultSort: {
      id: 'createdAt',
      desc: true,
    },
    buildFilterParams: () => ({}),
    initialPageSize: 10,
  });

  const {
    data: signups,
    status,
    isError,
    error,
  } = useInviteCodeSubscriptions({
    id: inviteCodeId,
    searchParams: {
      'page[number]': controller.searchParams['page[number]'],
      'page[size]': controller.searchParams['page[size]'],
    },
  });

  useEffect(() => {
    if (isError && error) {
      toastError(error.message || 'Failed to load signups');
    }
  }, [isError, error]);

  return (
    <ResourceTableView<RegistrationCodeSubscription>
      label="Signups"
      controller={controller}
      data={signups?.data ?? []}
      meta={signups?.meta}
      status={status}
      summaryIcon={<Users className="h-4 w-4 text-zinc-500" />}
      summaryLabel="Total Signups: "
      onRowClick={(row) => navigate(`/members/${row.fishId}`)}
    />
  );
}

export default function InviteCodesDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const {
    data: inviteCode,
    status,
    isError,
  } = useInviteCode({ id: id ?? '', enabled: !!id });
  const usage = useInviteCodeUsage({ id: id ?? '', enabled: !!id });

  if (isError) {
    return <NotFound />;
  }
  if (status === 'pending' || !inviteCode) {
    return <div>Loading...</div>;
  }

  const isDeleted = !!inviteCode.deletedAt;
  const terms = toInviteCodeFormValues(inviteCode);
  const renewalTerm = (description: string | null, window: WindowValues) => ({
    renewal: description,
    renewalLasts: description ? describeWindow(window) : null,
  });
  const pricingRows = [
    {
      label: 'Membership fee',
      signup: describeDiscount(terms.signupFee),
      ...renewalTerm(describeDiscount(terms.renewalFee), terms.renewalFee),
    },
    {
      label: 'Wallet',
      signup: describeDiscount(terms.signupWallet),
      ...renewalTerm(describeDiscount(terms.renewalWallet), terms.renewalWallet),
    },
    {
      label: 'Wallet credit adjustment',
      signup: describeAdjustment(terms.signupCredit.amount),
      ...renewalTerm(
        describeAdjustment(terms.renewalCredit.amount),
        terms.renewalCredit,
      ),
    },
  ];
  const partnerReferralCodes = usage.data?.partnerReferralCodes ?? [];

  return (
    <div className="w-full">
      <Breadcrumbs
        navList={[
          { name: 'Admin', href: '/' },
          { name: 'Invite Codes', href: '/invite-codes' },
          { name: inviteCode.code, href: `/invite-codes/${inviteCode.id}` },
        ]}
      />

      <div className="mt-8 mb-4 flex items-end">
        <Heading>{inviteCode.code}</Heading>
        <p className="mx-2 mb-1 text-sm font-bold text-zinc-500">
          {isDeleted ? 'DELETED INVITE CODE' : 'INVITE CODE'}
        </p>
        {!isDeleted && (
          <ShowFor permissions={['REGISTRATION_CODES_WRITE']}>
            <div className="ml-auto flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSheetOpen(true)}
              >
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-red-600"
                onClick={() => setDeleteDialogOpen(true)}
              >
                Delete
              </Button>
            </div>
          </ShowFor>
        )}
      </div>

      <div className="flex flex-col space-y-8">
        <div>
          <div className="mb-2">
            <Heading level="h3">Details</Heading>
          </div>
          <Card>
            <CardContent>
              <div className="grid grid-cols-2 gap-x-8 gap-y-4 sm:grid-cols-3">
                <DetailField label="Promo Details" className="col-span-2 sm:col-span-3">
                  {inviteCode.promoDetails?.length ? (
                    inviteCode.promoDetails.map((detail, index) => (
                      <div key={index}>{detail}</div>
                    ))
                  ) : (
                    <span className="text-zinc-400">No promo details</span>
                  )}
                </DetailField>
                <DetailField label="Allowed Tiers" className="col-span-2 sm:col-span-3">
                  {inviteCode.allowedTiers?.length ? (
                    inviteCode.allowedTiers.map((tier) => tier.title).join(', ')
                  ) : (
                    <span className="text-zinc-400">-</span>
                  )}
                </DetailField>
                <DetailField label="Created On">
                  {DateTime.fromISO(inviteCode.createdAt).toLocaleString(
                    DateTime.DATETIME_MED,
                  )}
                </DetailField>
                <DetailField label="Created By">
                  {inviteCode.createdByUser?.name || '-'}
                </DetailField>
                <DetailField label="Last Updated">
                  {DateTime.fromISO(inviteCode.updatedAt).toLocaleString(
                    DateTime.DATETIME_MED,
                  )}
                  {inviteCode.updatedByUser?.name
                    ? ` by ${inviteCode.updatedByUser.name}`
                    : ''}
                </DetailField>
                <DetailField label="Active Members">
                  {usage.data ? usage.data.activeSubscriptionCount : '-'}
                </DetailField>
              </div>
            </CardContent>
          </Card>
        </div>

        <div>
          <div className="mb-2">
            <Heading level="h3">Pricing</Heading>
          </div>
          <Card>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Term</TableHead>
                    <TableHead>Signup</TableHead>
                    <TableHead>Renewal</TableHead>
                    <TableHead>Renewal lasts</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pricingRows.map((row) => (
                    <TableRow key={row.label}>
                      <TableCell className="font-bold text-zinc-500">
                        {row.label}
                      </TableCell>
                      <TableCell>
                        <PricingValue value={row.signup} />
                      </TableCell>
                      <TableCell>
                        <PricingValue value={row.renewal} />
                      </TableCell>
                      <TableCell>
                        <PricingValue value={row.renewalLasts} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        <div>
          <div className="mb-2">
            <Heading level="h3">Linked Partner Codes</Heading>
          </div>
          <Card>
            <CardContent>
              {usage.isError ? (
                <div className="text-sm text-red-700">
                  Unable to load linked partner codes: {usage.error.message}
                </div>
              ) : partnerReferralCodes.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {partnerReferralCodes.map((partnerReferralCode) => (
                      <TableRow key={partnerReferralCode.id}>
                        <TableCell>
                          <Link
                            to={`/partner-referral-codes/${partnerReferralCode.id}`}
                            className="text-primary font-mono hover:underline"
                          >
                            {partnerReferralCode.code}
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="py-4 text-center text-sm text-zinc-500">
                  {usage.data
                    ? 'No partner code offers this invite code.'
                    : 'Loading...'}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <InviteCodeSignupList inviteCodeId={inviteCode.id} />
      </div>

      <InviteCodeSheet
        inviteCode={inviteCode}
        isOpen={sheetOpen}
        setIsOpen={setSheetOpen}
        onSuccess={() => toastSuccess(`"${inviteCode.code}" has been updated.`)}
      />

      <DeleteInviteCodeDialog
        inviteCode={inviteCode}
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onDeleted={() => navigate('/invite-codes')}
      />
    </div>
  );
}
